
import React, { useState, useEffect } from 'react';
import { Card, Button, Badge } from '../components/UI';
import { apiRequest } from '../utils/api';
import { useAuth } from '../components/AuthContext';
import { useLeague } from '../components/LeagueContext';
import { UserRole } from '../types';
import { Navigate } from 'react-router-dom';
import { TestTube, Activity, Gauge, TrendingUp, Trophy, Flag, Shield, Zap, AlertTriangle, Target, Star, ArrowUp, ArrowDown, Users, Timer } from 'lucide-react';

// Shared FormChart component
export const FormChart: React.FC<{ history: any[], height?: number }> = ({ history, height = 80 }) => {
    if (history.length < 2) return <div className="text-[9px] text-gray-700 text-center py-4">Zu wenig Daten</div>;
    const W = 280, H = height, padX = 20, padY = 10;
    const maxPos = 20;
    const points = history.map((h: any, i: number) => {
        const x = padX + (i / (history.length - 1)) * (W - 2 * padX);
        const pos = h.status === 'DNF' ? maxPos : (h.position || maxPos);
        const y = padY + ((pos - 1) / (maxPos - 1)) * (H - 2 * padY);
        return { x, y, pos, status: h.status, track: h.track, round: h.round_number };
    });
    const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
    const areaPath = `${linePath} L${points[points.length-1].x},${H} L${points[0].x},${H} Z`;

    return (
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height }}>
            <defs>
                <linearGradient id="formGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22c55e" stopOpacity="0.2"/>
                    <stop offset="100%" stopColor="#22c55e" stopOpacity="0"/>
                </linearGradient>
            </defs>
            {/* Grid lines */}
            {[1, 5, 10, 15, 20].map(p => {
                const y = padY + ((p - 1) / (maxPos - 1)) * (H - 2 * padY);
                return <g key={p}><line x1={padX} y1={y} x2={W-padX} y2={y} stroke="#1f2937" strokeWidth="0.5"/><text x={4} y={y+3} fill="#374151" fontSize="6" fontFamily="monospace">P{p}</text></g>;
            })}
            {/* Area fill */}
            <path d={areaPath} fill="url(#formGrad)"/>
            {/* Line */}
            <path d={linePath} fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            {/* Dots */}
            {points.map((p, i) => (
                <g key={i}>
                    <circle cx={p.x} cy={p.y} r={p.status === 'DNF' ? 4 : 3.5} fill={p.status === 'DNF' ? '#ef4444' : p.pos <= 3 ? '#facc15' : p.pos <= 10 ? '#22c55e' : '#6b7280'} stroke="#0a0a10" strokeWidth="1.5"/>
                    <text x={p.x} y={H - 2} textAnchor="middle" fill="#4b5563" fontSize="5.5" fontFamily="monospace">R{p.round}</text>
                </g>
            ))}
        </svg>
    );
};

// Shared StatBar
export const StatBar: React.FC<{label: string, value: number, max?: number, color: string, icon: React.ReactNode, suffix?: string}> = ({label, value, max = 100, color, icon, suffix = '%'}) => (
    <div className="flex items-center gap-3">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{backgroundColor: `${color}15`}}>
            <div style={{color}}>{icon}</div>
        </div>
        <div className="flex-1">
            <div className="flex justify-between items-baseline mb-1">
                <span className="text-[9px] text-gray-500 font-bold uppercase tracking-wider">{label}</span>
                <span className="font-mono font-bold text-xs text-white">{Number.isInteger(value) ? value : value.toFixed(1)}{suffix}</span>
            </div>
            <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-700" style={{width: `${Math.min(100, (value/max)*100)}%`, backgroundColor: color}}/>
            </div>
        </div>
    </div>
);

const PerformancePass: React.FC<{ userId: string, userName: string }> = ({ userId, userName }) => {
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        apiRequest(`driver_profile.php?id=${userId}`).then(res => setData(res)).catch(() => setData(null)).finally(() => setLoading(false));
    }, [userId]);

    if (loading) return <div className="p-20 text-center"><Activity className="animate-pulse mx-auto text-gray-600" size={32}/></div>;
    if (!data?.stats) return <div className="p-10 text-center text-gray-600 text-sm">Keine Daten.</div>;

    const s = data.stats; const d = data.discipline || {}; const team = data.team || {};
    const teamColor = team.color || '#666';
    const races = s.races_driven || 0; const points = parseFloat(s.points) || 0;
    const avgPts = races > 0 ? points / races : 0;
    const finishRate = races > 0 ? ((races - s.dnfs) / races * 100) : 0;
    const podiumRate = races > 0 ? (s.podiums / races * 100) : 0;
    const cleanRate = races > 0 ? ((s.clean_races||0) / races * 100) : 0;
    const history = data.history || [];
    const dotdCount = (data.badges || []).filter((b: any) => b.name?.toLowerCase().includes('dotd') || b.name?.toLowerCase().includes('driver of')).length;

    const calcGrade = () => {
        let score = 0;
        score += Math.min(30, avgPts * 1.2);
        score += Math.min(15, s.wins * 5);
        score += Math.min(10, podiumRate * 0.2);
        score += Math.min(15, finishRate * 0.15);
        score += Math.min(10, cleanRate * 0.1);
        score -= Math.min(15, (d.penalty_points || 0) * 3);
        if (s.best_pos === 1) score += 10; else if (s.best_pos <= 3) score += 5;
        score = Math.min(100, Math.max(0, Math.round(score)));
        if (score >= 90) return { grade: 'S+', color: '#fbbf24', bg: 'from-yellow-500/20 to-amber-600/10' };
        if (score >= 80) return { grade: 'S', color: '#facc15', bg: 'from-yellow-500/10 to-amber-500/5' };
        if (score >= 70) return { grade: 'A', color: '#4ade80', bg: 'from-green-500/10 to-emerald-500/5' };
        if (score >= 55) return { grade: 'B', color: '#60a5fa', bg: 'from-blue-500/10 to-blue-600/5' };
        if (score >= 40) return { grade: 'C', color: '#9ca3af', bg: 'from-gray-500/10 to-gray-600/5' };
        return { grade: 'D', color: '#ef4444', bg: 'from-red-500/10 to-red-600/5' };
    };
    const grade = calcGrade();

    return (
        <div className="w-full max-w-lg mx-auto space-y-4">
            {/* HEADER */}
            <div className={`relative overflow-hidden rounded-2xl border border-gray-800/60 bg-gradient-to-br ${grade.bg} p-6`}>
                <div className="absolute top-0 left-0 w-full h-1" style={{backgroundColor: teamColor}}/>
                <div className="absolute top-4 right-4 opacity-5"><Activity size={120}/></div>
                <div className="relative z-10 flex justify-between items-start">
                    <div>
                        <div className="text-[8px] text-gray-500 font-bold uppercase tracking-[0.3em]">Season Performance Pass</div>
                        <h2 className="text-3xl font-black text-white uppercase italic mt-1">{userName}</h2>
                        <div className="flex items-center gap-2 mt-1"><div className="w-2 h-2 rounded-full" style={{backgroundColor: teamColor}}/><span className="text-xs font-bold" style={{color: teamColor}}>{team.name || 'Free Agent'}</span></div>
                    </div>
                    <div className="text-right"><div className="text-7xl font-black leading-none" style={{color: grade.color}}>{grade.grade}</div></div>
                </div>
                <div className="relative z-10 grid grid-cols-4 gap-2 mt-6">
                    {[{val:points,label:'Punkte',icon:<Star size={10}/>},{val:s.wins,label:'Siege',icon:<Trophy size={10}/>},{val:s.podiums,label:'Podien',icon:<Flag size={10}/>},{val:races,label:'Rennen',icon:<Target size={10}/>}].map((item,i) => (
                        <div key={i} className="bg-black/20 rounded-lg p-2 text-center backdrop-blur-sm">
                            <div className="text-lg font-black text-white">{item.val}</div>
                            <div className="text-[7px] text-gray-500 font-bold uppercase tracking-wider flex items-center justify-center gap-1">{item.icon}{item.label}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* PERFORMANCE BARS */}
            <div className="rounded-2xl border border-gray-800/40 bg-[#12121a] p-5 space-y-4">
                <div className="text-[9px] text-gray-600 font-bold uppercase tracking-[0.2em] mb-1">Performance Analyse</div>
                <StatBar label="Zielrate" value={finishRate} color="#4ade80" icon={<Flag size={14}/>}/>
                <StatBar label="Podiumrate" value={podiumRate} color="#facc15" icon={<Trophy size={14}/>}/>
                <StatBar label="Clean Race Rate" value={cleanRate} color="#60a5fa" icon={<Shield size={14}/>}/>
                <StatBar label="Punkte Ø / Rennen" value={avgPts} max={25} color="#a78bfa" icon={<TrendingUp size={14}/>} suffix=" Pkt"/>
            </div>

            {/* FORM CHART */}
            <div className="rounded-2xl border border-gray-800/40 bg-[#12121a] p-5">
                <div className="text-[9px] text-gray-600 font-bold uppercase tracking-[0.2em] mb-3">Formkurve</div>
                <FormChart history={history} height={90}/>
            </div>

            {/* RECORDS + DISCIPLINE */}
            <div className="grid grid-cols-2 gap-4">
                <div className="rounded-2xl border border-gray-800/40 bg-[#12121a] p-4 space-y-3">
                    <div className="text-[9px] text-gray-600 font-bold uppercase tracking-[0.2em]">Bestmarken</div>
                    <div><div className="text-[8px] text-gray-600 uppercase">Beste Position</div><div className="text-2xl font-black text-yellow-400">P{s.best_pos||'-'}</div></div>
                    <div><div className="text-[8px] text-gray-600 uppercase">DNFs</div><div className={`text-xl font-black ${s.dnfs===0?'text-green-400':'text-red-400'}`}>{s.dnfs}</div></div>
                    {dotdCount > 0 && <div><div className="text-[8px] text-gray-600 uppercase">DotD</div><div className="text-xl font-black text-purple-400">{dotdCount}x ★</div></div>}
                </div>
                <div className="rounded-2xl border border-gray-800/40 bg-[#12121a] p-4">
                    <div className="text-[9px] text-gray-600 font-bold uppercase tracking-[0.2em] mb-3">Disziplin</div>
                    <div className="space-y-2">
                        <div className="flex justify-between"><span className="text-[9px] text-gray-600">Strafpunkte</span><span className={`font-mono font-bold text-sm ${(d.penalty_points||0)===0?'text-green-400':'text-red-400'}`}>{d.penalty_points||0}</span></div>
                        <div className="flex justify-between"><span className="text-[9px] text-gray-600">Verpasst</span><span className={`font-mono font-bold text-sm ${(d.attendance_strikes||0)===0?'text-green-400':'text-yellow-400'}`}>{d.attendance_strikes||0}</span></div>
                        <div className="flex justify-between"><span className="text-[9px] text-gray-600">Clean Races</span><span className="font-mono font-bold text-sm text-blue-400">{s.clean_races||0}</span></div>
                    </div>
                </div>
            </div>

            {/* RACE HISTORY TILES */}
            {history.length > 0 && (
                <div className="rounded-2xl border border-gray-800/40 bg-[#12121a] p-5">
                    <div className="text-[9px] text-gray-600 font-bold uppercase tracking-[0.2em] mb-3">Saison-Verlauf</div>
                    <div className="flex flex-wrap gap-1.5">
                        {history.map((h: any, i: number) => {
                            const pos = h.position || 20;
                            const bg = h.status==='DNF'?'bg-red-500/20 text-red-400 border-red-500/30':pos===1?'bg-yellow-500/20 text-yellow-400 border-yellow-500/30':pos<=3?'bg-green-500/20 text-green-400 border-green-500/30':pos<=10?'bg-blue-500/10 text-blue-400 border-blue-500/20':'bg-gray-800/50 text-gray-500 border-gray-700/30';
                            return <div key={i} className={`w-8 h-8 rounded-lg border flex items-center justify-center text-[10px] font-black ${bg}`} title={`R${h.round_number}: P${pos} - ${h.track}`}>{h.status==='DNF'?'✗':pos}</div>;
                        })}
                    </div>
                </div>
            )}
            <div className="text-center text-[8px] text-gray-800 font-mono">SDC Performance Pass • {new Date().toLocaleDateString('de-DE')}</div>
        </div>
    );
};

export const TestStudio: React.FC = () => {
    const { user } = useAuth();
    const { drivers } = useLeague();
    const [selectedUserId, setSelectedUserId] = useState('');
    if (!user || !user.roles.includes(UserRole.WEBADMIN)) return <Navigate to="/" />;
    useEffect(() => { if(user && !selectedUserId) setSelectedUserId(user.id); }, [user]);
    const selectedDriver = drivers.find(d => d.id === selectedUserId);
    return (
        <div className="space-y-6 animate-fade-in pb-20">
            <div><h1 className="text-2xl font-bold uppercase italic flex items-center gap-3 text-f1-red"><TestTube size={32}/> Test Studio</h1><p className="text-gray-400 text-sm">Performance Pass Generator</p></div>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                <Card title="Fahrer wählen"><select className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white text-sm" value={selectedUserId} onChange={e=>setSelectedUserId(e.target.value)}>{drivers.map(d=><option key={d.id} value={d.id}>{d.username}</option>)}</select></Card>
                <div className="lg:col-span-3">{selectedDriver ? <PerformancePass userId={selectedUserId} userName={selectedDriver.username}/> : <div className="text-gray-600 text-center p-10">Wähle einen Fahrer.</div>}</div>
            </div>
        </div>
    );
};
